// Extracted JavaScript from MegaMenu.astro - works with existing HTML structure
// Use globally available modules from main.ts
const Headroom = window.Headroom;

export class MegaMenu {
    activeMenu: string | null;
    isHovering: boolean;
    megaDropdownElement: HTMLElement | null;
    navElement: HTMLElement | null;
    headroom: any;
    activeSidebarItem: string | null;
    activeSubSidebarItem: string | null;
    showSubSidebar: boolean;
    mobileMenuOpen: boolean;
    mobileCurrentLevel: string;
    mobileActiveMenu: string | null;
    mobileActiveSidebar: string | null;
    mobileActiveSubsidebar: string | null;

    // Manual animation state for mobile drawer
    private mobileAnimRAF: number | null = null;

    // Guard to avoid attaching duplicate listeners
    private mobileListenersBound: boolean = false;
    private desktopListenersBound: boolean = false;

    // Focus trap handler for mobile menu
    private mobileMenuFocusTrapHandler: ((e: KeyboardEvent) => void) | null = null;

    constructor() {
        // State variables
        this.activeMenu = null;
        this.isHovering = false;
        this.megaDropdownElement = null;
        this.navElement = null;
        this.headroom = null;
        this.activeSidebarItem = null;
        this.activeSubSidebarItem = null;
        this.showSubSidebar = false;
        this.mobileMenuOpen = false;

        // Mobile navigation state
        this.mobileCurrentLevel = 'main'; // 'main', 'sidebar', 'subsidebar'
        this.mobileActiveMenu = null;
        this.mobileActiveSidebar = null;
        this.mobileActiveSubsidebar = null;

        this.mobileAnimRAF = null;
    }

    handleMouseEnter(menuId: string) {
        // Show the appropriate hardcoded menu content
        this.activeMenu = menuId;
        this.isHovering = true;

        // Hide all menu content sections and set their tabindex to -1
        const allMenuContent = document.querySelectorAll('.menu-content');
        allMenuContent.forEach(content => {
            (content as HTMLElement).setAttribute('hidden', '');
            (content as HTMLElement).style.display = 'none';
            // Remove all links from tab order
            this.updateDropdownTabindex(content as HTMLElement, true);
        });

        // Show the specific menu content
        const targetMenu = document.querySelector(`[data-menu="${menuId}"]`) as HTMLElement;
        if (targetMenu) {
            targetMenu.removeAttribute('hidden');
            targetMenu.style.display = 'block';

            // Make all links in this dropdown accessible
            this.updateDropdownTabindex(targetMenu, false);

            // Set up event listeners for this menu's sidebar items
            this.setupMegaMenuEventListeners();

            // Set default active sidebar item (first one with content)
            const firstSidebarItem = targetMenu.querySelector('[data-sidebar-id]') as HTMLElement;
            if (firstSidebarItem) {
                const sidebarId = firstSidebarItem.dataset.sidebarId;
                if (sidebarId) {
                    this.handleSidebarItemHover(sidebarId);
                }
            }
        }

        // Update mega menu styles
        this.updateBackdropVisibility();
        this.updateMegaDropdownVisibility();
    }

    handleMouseLeave() {
        this.isHovering = false;
        setTimeout(() => {
            if (!this.isHovering) {
                this.activeMenu = null;
                this.activeSidebarItem = null;
                this.activeSubSidebarItem = null;
                this.showSubSidebar = false;

                // Hide all menu content
                const allMenuContent = document.querySelectorAll('.menu-content');
                allMenuContent.forEach(content => {
                    (content as HTMLElement).style.display = 'none';

                    // Remove all links from tab order when hidden
                    this.updateDropdownTabindex(content as HTMLElement, true);

                    // Reset sub-sidebar state
                    const subSidebarContainer = content.querySelector('.sub-sidebar-container') as HTMLElement;
                    const sidebarLayout = content.querySelector('.sidebar-layout') as HTMLElement;
                    if (subSidebarContainer) {
                        subSidebarContainer.style.display = 'none';
                    }
                    if (sidebarLayout) {
                        sidebarLayout.classList.remove('triple-layout');
                    }
                });

                this.updateBackdropVisibility();
                this.updateMegaDropdownVisibility();
            }
        }, 150);
    }

    handleMegaMenuEnter() {
        this.isHovering = true;
    }

    handleMegaMenuLeave() {
        this.isHovering = false;
        setTimeout(() => {
            if (!this.isHovering) {
                this.activeMenu = null;
                this.activeSidebarItem = null;
                this.activeSubSidebarItem = null;
                this.showSubSidebar = false;

                // Hide all menu content
                const allMenuContent = document.querySelectorAll('.menu-content');
                allMenuContent.forEach(content => {
                    (content as HTMLElement).style.display = 'none';

                    // Reset sub-sidebar state
                    const subSidebarContainer = content.querySelector('.sub-sidebar-container') as HTMLElement;
                    const sidebarLayout = content.querySelector('.sidebar-layout') as HTMLElement;
                    if (subSidebarContainer) {
                        subSidebarContainer.style.display = 'none';
                    }
                    if (sidebarLayout) {
                        sidebarLayout.classList.remove('triple-layout');
                    }
                });

                this.updateBackdropVisibility();
                this.updateMegaDropdownVisibility();
            }
        }, 150);
    }

    handleSidebarItemHover(itemId: string) {
        this.activeSidebarItem = itemId;

        // Find the current active menu content
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (!currentMenuContent) return;

        // Update ARIA attributes for all sidebar items
        const allSidebarItems = currentMenuContent.querySelectorAll('[data-sidebar-id]');
        allSidebarItems.forEach(item => {
            const sidebarId = (item as HTMLElement).dataset.sidebarId;
            const isActive = sidebarId === itemId;
            const hasSubsidebar = (item as HTMLElement).dataset.hasSubsidebar === 'true';

            // Update aria-current for active item - use 'true' for better screen reader support
            if (isActive) {
                item.setAttribute('aria-current', 'true');
                // Add active class for visual styling
                item.classList.add('active');
                if (hasSubsidebar) {
                    item.setAttribute('aria-expanded', 'true');
                }
            } else {
                item.removeAttribute('aria-current');
                // Remove active class
                item.classList.remove('active');
                if (hasSubsidebar) {
                    item.setAttribute('aria-expanded', 'false');
                }
            }
        });

        // Prefer DOM-driven detection of sub-sidebar by matching container for this item
        const matchedSubContainers = Array.from(currentMenuContent.querySelectorAll('.sub-sidebar-container')) as HTMLElement[];
        const targetedContainer = matchedSubContainers.find(c => c.getAttribute('data-subsidebar-for') === itemId) || null;

        // Back-compat: also support explicit flag on the sidebar item
        const sidebarItem = currentMenuContent.querySelector(`[data-sidebar-id="${itemId}"]`) as HTMLElement;
        const hasSubSidebar = !!targetedContainer || sidebarItem?.dataset.hasSubsidebar === 'true';

        // Handle sub-sidebar visibility
        const sidebarLayout = currentMenuContent.querySelector('.sidebar-layout') as HTMLElement;

        if (hasSubSidebar) {
            this.showSubSidebar = true;

            // Hide all sub-sidebar containers, then show only the targeted one (or first available)
            matchedSubContainers.forEach(el => (el.style.display = 'none'));
            const containerToShow = targetedContainer || matchedSubContainers[0] || null;
            if (containerToShow) {
                containerToShow.style.display = 'block';
            }
            if (sidebarLayout) {
                sidebarLayout.classList.add('triple-layout');
            }

            // Set default active sub-sidebar item (first one available in the shown container)
            const firstSubSidebarItem = containerToShow?.querySelector('[data-subsidebar-id]') as HTMLElement;
            const firstSubSidebarId = firstSubSidebarItem?.dataset.subsidebarId;
            if (firstSubSidebarId) {
                this.activeSubSidebarItem = firstSubSidebarId;
                this.handleSubSidebarItemHover(firstSubSidebarId);
            }
        } else {
            this.showSubSidebar = false;
            this.activeSubSidebarItem = null;
            // Hide all sub-sidebar containers when not applicable
            matchedSubContainers.forEach(el => (el.style.display = 'none'));
            if (sidebarLayout) {
                sidebarLayout.classList.remove('triple-layout');
            }

            // Show the specific sidebar content
            this.showSidebarContent(itemId);
        }

        // Update active states
        this.updateSidebarActiveStates(itemId);
    }

    handleSubSidebarItemHover(itemId: string) {
        this.activeSubSidebarItem = itemId;

        // Show the specific sub-sidebar content
        this.showSubSidebarContent(itemId);

        // Update active states and ARIA attributes
        this.updateSubSidebarActiveStates(itemId);

        // Update ARIA current attribute for screen readers
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (currentMenuContent) {
            const allSubSidebarItems = currentMenuContent.querySelectorAll('[data-subsidebar-id]');
            allSubSidebarItems.forEach(item => {
                const subsidebarId = (item as HTMLElement).dataset.subsidebarId;
                if (subsidebarId === itemId) {
                    item.setAttribute('aria-current', 'true');
                    item.classList.add('active');
                } else {
                    item.removeAttribute('aria-current');
                    item.classList.remove('active');
                }
            });
        }
    }

    showSidebarContent(sidebarId: string) {
        // Find the current active menu content
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (!currentMenuContent) return;

        // Hide all sidebar content sections within this menu
        const allSidebarContent = currentMenuContent.querySelectorAll('.sidebar-content');
        allSidebarContent.forEach(content => {
            (content as HTMLElement).style.display = 'none';
        });

        // Show the specific sidebar content
        const targetContent = currentMenuContent.querySelector(`[data-content="${sidebarId}"]`) as HTMLElement;
        if (targetContent) {
            targetContent.style.display = 'block';
        }
    }

    showSubSidebarContent(subSidebarId: string) {
        // Find the current active menu content
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (!currentMenuContent) return;

        // Hide all sidebar content sections within this menu
        const allSidebarContent = currentMenuContent.querySelectorAll('.sidebar-content');
        allSidebarContent.forEach(content => {
            (content as HTMLElement).style.display = 'none';
        });

        // Show the specific sub-sidebar content, preferring the one matching the active parent sidebar
        const candidates = Array.from(currentMenuContent.querySelectorAll(`[data-content="${subSidebarId}"]`)) as HTMLElement[];
        let targetContent: HTMLElement | null = null;
        for (const el of candidates) {
            const parentAttr = el.getAttribute('data-parent-sidebar');
            if (!parentAttr || parentAttr === this.activeSidebarItem) {
                targetContent = el;
                break;
            }
        }
        if (!targetContent) {
            targetContent = candidates[0] || null;
        }
        if (targetContent) {
            targetContent.style.display = 'block';
        }
    }

    updateSidebarActiveStates(activeId: string) {
        // Find the current active menu content
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (!currentMenuContent) return;

        // Remove active class from all sidebar items
        const allSidebarItems = currentMenuContent.querySelectorAll('[data-sidebar-id]');
        allSidebarItems.forEach(item => {
            item.classList.remove('active');
        });

        // Add active class to the current item
        const activeItem = currentMenuContent.querySelector(`[data-sidebar-id="${activeId}"]`);
        if (activeItem) {
            activeItem.classList.add('active');
        }
    }

    updateSubSidebarActiveStates(activeId: string) {
        // Find the current active menu content
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (!currentMenuContent) return;

        // Determine the relevant (visible) sub-sidebar container for the active parent sidebar
        const subContainers = Array.from(currentMenuContent.querySelectorAll('.sub-sidebar-container')) as HTMLElement[];
        let container = subContainers.find(c => c.getAttribute('data-subsidebar-for') === this.activeSidebarItem) || null;
        if (!container) {
            // Fallback to the one currently displayed
            container = subContainers.find(c => (c as HTMLElement).style.display !== 'none') || null;
        }
        if (!container) {
            // If still not found, bail out safely
            return;
        }

        // Remove active class only within the relevant container
        const itemsInContainer = container.querySelectorAll('[data-subsidebar-id]');
        itemsInContainer.forEach(item => item.classList.remove('active'));

        // Add active class to the current item inside this container
        const activeItemEl = container.querySelector(`[data-subsidebar-id="${activeId}"]`);
        if (activeItemEl) {
            (activeItemEl as HTMLElement).classList.add('active');
        }
    }

    updateBackdropVisibility() {
        const backdrop = document.querySelector('[data-backdrop]') as HTMLElement;
        if (backdrop) {
            // Show backdrop when either desktop mega menu is active OR mobile menu is open
            const shouldShow = this.activeMenu || this.mobileMenuOpen;
            if (shouldShow) {
                backdrop.removeAttribute('hidden');
                backdrop.style.display = 'block';
                backdrop.style.opacity = '1';
                document.body.style.overflow = 'hidden';
            } else {
                backdrop.setAttribute('hidden', '');
                backdrop.style.display = 'none';
                backdrop.style.opacity = '0';
                document.body.style.overflow = '';
            }
        }
    }

    updateMegaDropdownVisibility() {
        if (this.megaDropdownElement) {
            if (this.activeMenu) {
                this.megaDropdownElement.removeAttribute('hidden');
                this.megaDropdownElement.style.display = 'block';
            } else {
                this.megaDropdownElement.setAttribute('hidden', '');
                this.megaDropdownElement.style.display = 'none';
            }
        }
    }

    setupMegaMenuEventListeners() {
        if (!this.megaDropdownElement) return;
        if (this.desktopListenersBound) return; // avoid duplicate bindings on repeated opens

        // Setup sidebar item hover listeners
        const sidebarItems = this.megaDropdownElement.querySelectorAll('[data-sidebar-id]');
        sidebarItems.forEach(item => {
            item.addEventListener('mouseenter', (e) => {
                const sidebarId = (e.target as HTMLElement).closest('[data-sidebar-id]')?.getAttribute('data-sidebar-id');
                if (sidebarId) {
                    this.handleSidebarItemHover(sidebarId);
                }
            });
            item.addEventListener('click', (e) => {
                e.preventDefault();
            });
        });

        // Setup sub-sidebar item hover listeners
        const subSidebarItems = this.megaDropdownElement.querySelectorAll('[data-subsidebar-id]');
        subSidebarItems.forEach(item => {
            item.addEventListener('mouseenter', (e) => {
                const subSidebarId = (e.target as HTMLElement).closest('[data-subsidebar-id]')?.getAttribute('data-subsidebar-id');
                if (subSidebarId) {
                    this.handleSubSidebarItemHover(subSidebarId);
                }
            });
            item.addEventListener('click', (e) => {
                e.preventDefault();
            });
        });

        this.desktopListenersBound = true;
    }

    // Mobile menu methods
    toggleMobileMenu() {
        this.mobileMenuOpen = !this.mobileMenuOpen;
        if (this.mobileMenuOpen) {
            this.setupMobileMenuEventListeners();
            // Show the main level when opening
            this.mobileCurrentLevel = 'main';
            this.showMobileLevel('main');
            // Announce to screen readers
            this.announceToScreenReader('Mobilni meni odprt');
        } else {
            // Reset mobile navigation state when closing
            this.mobileCurrentLevel = 'main';
            this.mobileActiveMenu = null;
            this.mobileActiveSidebar = null;
            this.mobileActiveSubsidebar = null;
            // Announce to screen readers
            this.announceToScreenReader('Mobilni meni zaprt');
        }
        this.updateMobileMenuButtonARIA(this.mobileMenuOpen);
        this.updateMobileMenuVisibility();
        this.updateBackdropVisibility(); // Update backdrop when mobile menu is toggled
    }

    updateMobileMenuVisibility() {
        const mobileMenu = document.querySelector('[data-mobile-menu]') as HTMLElement;
        const menuNav = document.querySelector('[data-mega-nav]') as HTMLElement;
        if (!mobileMenu) return;

        const isAlternate = document.body.classList.contains('has-alternate-header');
        const targetTop = isAlternate ? 135 : 100; // hard anchor from window top
        const sideInset = 11; // match Astro: left/right 11px (px-3)
        const bottomGap = 12; // keep 13px see-through space at the bottom
        const fullHeight = Math.max(0, window.innerHeight - targetTop - bottomGap);

        // cancel any running RAF
        if (this.mobileAnimRAF) {
            cancelAnimationFrame(this.mobileAnimRAF);
            this.mobileAnimRAF = null;
        }

        const setDisplay = (show: boolean) => {
            if (show) {
                mobileMenu.removeAttribute('hidden');
                mobileMenu.style.display = 'flex';
                // Ensure proper fixed anchoring and side insets
                mobileMenu.style.position = 'fixed';
                mobileMenu.style.top = `${targetTop}px`;
                mobileMenu.style.left = `${sideInset}px`;
                mobileMenu.style.right = `${sideInset}px`;
                mobileMenu.style.width = '';
                mobileMenu.style.overflow = 'hidden';
            } else {
                mobileMenu.setAttribute('hidden', '');
                mobileMenu.style.display = 'none';
            }
        };

        const animateHeight = (from: number, to: number, duration = 280) => {
            const start = performance.now();
            const step = (now: number) => {
                const t = Math.min(1, (now - start) / duration);
                const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOut
                const current = Math.round(from + (to - from) * eased);
                mobileMenu.style.height = `${current}px`;
                if (t < 1) {
                    this.mobileAnimRAF = requestAnimationFrame(step);
                } else {
                    this.mobileAnimRAF = null;
                }
            };
            this.mobileAnimRAF = requestAnimationFrame(step);
        };

        // Remove any CSS animation classes/attributes
        mobileMenu.removeAttribute('data-animation-state');
        mobileMenu.classList.remove('animate-slideInFromHeader', 'animate-slideOutToHeader', 'animate-[slideInFromTop_0.5s_ease]');

        if (this.mobileMenuOpen) {
            // Open: from current height (or 0) to fullHeight
            const current = parseInt(mobileMenu.style.height || '0', 10) || 0;
            setDisplay(true);
            animateHeight(current, fullHeight);
            menuNav?.querySelector('[data-mega-nav-second-level]')?.classList.remove('rounded-b-[16px]');
            menuNav?.classList.add('no-borders');
        } else {
            // Close: from current height to 0, then hide
            const current = parseInt(mobileMenu.style.height || `${fullHeight}`, 10) || fullHeight;
            animateHeight(current, 0);
            setTimeout(() => {
                if (!this.mobileMenuOpen) {
                    setDisplay(false);
                    mobileMenu.style.height = '0px';
                }
            }, 300);
            menuNav?.querySelector('[data-mega-nav-second-level]')?.classList.add('rounded-b-[16px]');
            menuNav?.classList.remove('no-borders');
        }

        // Update hamburger icon visibility
        const hamburgerIcon = document.querySelector('[data-hamburger-icon]') as HTMLElement;
        const closeIcon = document.querySelector('[data-close-icon]') as HTMLElement;

        if (hamburgerIcon && closeIcon) {
            closeIcon.style.display = this.mobileMenuOpen ? 'block' : 'none';
            hamburgerIcon.style.display = this.mobileMenuOpen ? 'none' : 'block';
        }
    }

    setupMobileMenuEventListeners() {
        if (this.mobileListenersBound) return; // avoid duplicate bindings
        const mobileMenuContainer = document.querySelector('.mobile-menu-container');
        if (!mobileMenuContainer) return;

        // Setup mobile menu item click listeners
        const mobileMenuItems = mobileMenuContainer.querySelectorAll('[data-mobile-menu-id]');
        mobileMenuItems.forEach(item => {
            item.addEventListener('click', (e) => {
                const element = (e.target as HTMLElement).closest('[data-mobile-menu-id]') as HTMLElement;
                const menuId = element?.dataset.mobileMenuId;
                if (menuId) {
                    this.handleMobileMenuClick(menuId);
                }
            });
        });

        // Setup mobile sidebar item click listeners
        const mobileSidebarItems = mobileMenuContainer.querySelectorAll('[data-mobile-sidebar-id]');
        mobileSidebarItems.forEach(item => {
            item.addEventListener('click', (e) => {
                const element = (e.target as HTMLElement).closest('[data-mobile-sidebar-id]') as HTMLElement;
                const sidebarId = element?.dataset.mobileSidebarId;
                if (sidebarId) {
                    this.handleMobileSidebarClick(sidebarId);
                }
            });
        });

        // Setup mobile sub-sidebar item click listeners
        const mobileSubSidebarItems = mobileMenuContainer.querySelectorAll('[data-mobile-subsidebar-id]');
        mobileSubSidebarItems.forEach(item => {
            item.addEventListener('click', (e) => {
                const element = (e.target as HTMLElement).closest('[data-mobile-subsidebar-id]') as HTMLElement;
                const subSidebarId = element?.dataset.mobileSubsidebarId;
                if (subSidebarId) {
                    this.handleMobileSubSidebarClick(subSidebarId);
                }
            });
        });

        // Setup mobile back buttons (there might be multiple)
        const backButtons = mobileMenuContainer.querySelectorAll('[data-mobile-back]');
        backButtons.forEach(backButton => {
            backButton.addEventListener('click', () => this.goBackMobile());
        });

        // Setup mobile close items (items that close the menu directly)
        const closeItems = mobileMenuContainer.querySelectorAll('[data-mobile-close]');
        closeItems.forEach(item => {
            item.addEventListener('click', () => this.toggleMobileMenu());
        });

        this.mobileListenersBound = true;
    }

    handleMobileMenuClick(menuId: string) {
        this.mobileActiveMenu = menuId;

        // Dynamic navigation: Check what level exists for this menu item
        // Some menus like "Uporabno" go directly to content (four-column layout)
        // Others go to sidebar level first

        // Check if there's a sidebar level for this menu
        const sidebarLevel = document.querySelector(`[data-level="sidebar"][data-menu="${menuId}"]`);

        if (sidebarLevel) {
            // This menu has a sidebar level
            this.mobileCurrentLevel = 'sidebar';
            this.showMobileLevel('sidebar', menuId);
        } else {
            // Check if there's a direct content level for this menu
            const contentLevel = document.querySelector(`[data-level="content"][data-menu="${menuId}"]`);

            if (contentLevel) {
                // This menu goes directly to content (like "Uporabno")
                this.mobileCurrentLevel = 'content';
                this.showMobileLevel('content', menuId);
            } else {
                // No deeper level found, close the menu
                this.toggleMobileMenu();
            }
        }
    }

    handleMobileSidebarClick(sidebarId: string) {
        this.mobileActiveSidebar = sidebarId;

        // Dynamic navigation: Check what level exists for this item
        // This allows for flexible menu structures without hardcoding item IDs

        // Check if there's a sub-sidebar level for this item
        const subSidebarLevel = document.querySelector(`[data-level="subsidebar"][data-menu="${this.mobileActiveMenu}"][data-sidebar="${sidebarId}"]`);

        if (subSidebarLevel) {
            // This item has a sub-sidebar level
            this.mobileCurrentLevel = 'subsidebar';
            this.mobileActiveSubsidebar = null;
            this.showMobileLevel('subsidebar', this.mobileActiveMenu, sidebarId);
        } else {
            // Check if there's a content level for this item
            const contentLevel = document.querySelector(`[data-level="content"][data-menu="${this.mobileActiveMenu}"][data-sidebar="${sidebarId}"]`);

            if (contentLevel) {
                // This item has a content level
                this.mobileCurrentLevel = 'content';
                this.mobileActiveSubsidebar = null;
                this.showMobileLevel('content', this.mobileActiveMenu, sidebarId);
            } else {
                // No deeper level found, close the menu
                this.toggleMobileMenu();
            }
        }
    }

    handleMobileSubSidebarClick(subSidebarId: string) {
        this.mobileActiveSubsidebar = subSidebarId;

        // Check if there's a sub-content level for this item
        const subContentLevel = document.querySelector(`[data-level="subcontent"][data-menu="${this.mobileActiveMenu}"][data-sidebar="${this.mobileActiveSidebar}"][data-subsidebar="${subSidebarId}"]`);

        if (subContentLevel) {
            // This item has a sub-content level
            this.mobileCurrentLevel = 'subcontent';
            this.showMobileLevel('subcontent', this.mobileActiveMenu, this.mobileActiveSidebar, subSidebarId);
        } else {
            // No deeper level found, close the menu
            this.toggleMobileMenu();
        }
    }

    goBackMobile() {
        if (this.mobileCurrentLevel === 'subcontent') {
            this.mobileCurrentLevel = 'subsidebar';
            this.mobileActiveSubsidebar = null;
            this.showMobileLevel('subsidebar', this.mobileActiveMenu, this.mobileActiveSidebar);
        } else if (this.mobileCurrentLevel === 'content') {
            // Check if this content level came from a sidebar or directly from main
            const sidebarLevel = document.querySelector(`[data-level="sidebar"][data-menu="${this.mobileActiveMenu}"]`);

            if (sidebarLevel) {
                // Go back to sidebar level
                this.mobileCurrentLevel = 'sidebar';
                this.mobileActiveSidebar = null;
                this.showMobileLevel('sidebar', this.mobileActiveMenu);
            } else {
                // Go back to main level (for menus like "Uporabno")
                this.mobileCurrentLevel = 'main';
                this.mobileActiveMenu = null;
                this.showMobileLevel('main');
            }
        } else if (this.mobileCurrentLevel === 'subsidebar') {
            this.mobileCurrentLevel = 'sidebar';
            this.mobileActiveSidebar = null;
            this.showMobileLevel('sidebar', this.mobileActiveMenu);
        } else if (this.mobileCurrentLevel === 'sidebar') {
            this.mobileCurrentLevel = 'main';
            this.mobileActiveMenu = null;
            this.showMobileLevel('main');
        }
    }

    showMobileLevel(level: string, menuId?: string, sidebarId?: string, subSidebarId?: string) {
        // Hide all mobile menu levels
        const allLevels = document.querySelectorAll('.mobile-menu-level');
        allLevels.forEach(levelEl => {
            (levelEl as HTMLElement).style.display = 'none';
        });

        // Show the appropriate level
        if (level === 'main') {
            const mainLevel = document.querySelector('[data-level="main"]') as HTMLElement;
            if (mainLevel) {
                mainLevel.style.display = 'block';
            }
        } else if (level === 'sidebar' && menuId) {
            const sidebarLevel = document.querySelector(`[data-level="sidebar"][data-menu="${menuId}"]`) as HTMLElement;
            if (sidebarLevel) {
                sidebarLevel.style.display = 'block';
            }
        } else if (level === 'subsidebar' && menuId && sidebarId) {
            const subSidebarLevel = document.querySelector(`[data-level="subsidebar"][data-menu="${menuId}"][data-sidebar="${sidebarId}"]`) as HTMLElement;
            if (subSidebarLevel) {
                subSidebarLevel.style.display = 'block';
            }
        } else if (level === 'content' && menuId && !sidebarId) {
            // Support top-level content pages (e.g., Uporabno) that don't have sidebar
            const contentLevelTop = document.querySelector(`[data-level="content"][data-menu="${menuId}"]`) as HTMLElement;
            if (contentLevelTop) {
                contentLevelTop.style.display = 'block';
            }
        } else if (level === 'content' && menuId && sidebarId) {
            const contentLevel = document.querySelector(`[data-level="content"][data-menu="${menuId}"][data-sidebar="${sidebarId}"]`) as HTMLElement;
            if (contentLevel) {
                contentLevel.style.display = 'block';
            }
        } else if (level === 'subcontent' && menuId && sidebarId && subSidebarId) {
            const subContentLevel = document.querySelector(`[data-level="subcontent"][data-menu="${menuId}"][data-sidebar="${sidebarId}"][data-subsidebar="${subSidebarId}"]`) as HTMLElement;
            if (subContentLevel) {
                subContentLevel.style.display = 'block';
            }
        }
    }

    // Keyboard navigation methods
    updateMenuButtonARIA(button: HTMLElement, expanded: boolean) {
        button.setAttribute('aria-expanded', expanded.toString());
        const menuId = button.dataset.menuId;
        if (menuId) {
            const menuContent = document.getElementById(`menu-${menuId}`);
            if (menuContent) {
                if (expanded) {
                    menuContent.removeAttribute('hidden');
                    // Make all links in the dropdown accessible
                    this.updateDropdownTabindex(menuContent, false);
                    // Announce to screen readers
                    const buttonText = button.textContent?.trim() || 'Menu';
                    this.announceToScreenReader(`${buttonText} meni odprt`);
                } else {
                    menuContent.setAttribute('hidden', '');
                    // Remove dropdown links from tab order
                    this.updateDropdownTabindex(menuContent, true);
                }
            }
        }
    }

    updateDropdownTabindex(container: HTMLElement, remove: boolean) {
        // Get all focusable elements - generic selectors to support both a and button
        const allLinks = container.querySelectorAll('[data-sidebar-id], [data-subsidebar-id], a[href]');
        const allButtons = container.querySelectorAll('button:not([disabled]):not([data-sidebar-id]):not([data-subsidebar-id])');

        // Combine all elements
        const allFocusableElements = [
            ...Array.from(allLinks),
            ...Array.from(allButtons)
        ];

        allFocusableElements.forEach(element => {
            if (remove) {
                (element as HTMLElement).setAttribute('tabindex', '-1');
            } else {
                // If element is an anchor without href, ensure it has tabindex="0"
                if (element.tagName === 'A' && !element.hasAttribute('href')) {
                    (element as HTMLElement).setAttribute('tabindex', '0');
                } else {
                    (element as HTMLElement).removeAttribute('tabindex');
                }
            }
        });
    }

    handleMenuButtonKeydown(e: KeyboardEvent, button: HTMLElement, buttons: NodeListOf<Element>, index: number) {
        const key = e.key;
        let handled = false;

        switch (key) {
            case 'ArrowRight':
                e.preventDefault();
                const nextIndex = (index + 1) % buttons.length;
                (buttons[nextIndex] as HTMLElement).focus();
                handled = true;
                break;

            case 'ArrowLeft':
                e.preventDefault();
                const prevIndex = (index - 1 + buttons.length) % buttons.length;
                (buttons[prevIndex] as HTMLElement).focus();
                handled = true;
                break;

            case 'ArrowDown':
            case 'Enter':
            case ' ':
                e.preventDefault();
                const menuId = button.dataset.menuId;
                if (menuId) {
                    // Check if menu is already open
                    const isOpen = this.activeMenu === menuId;

                    if (isOpen) {
                        // Close the menu
                        this.closeActiveMenu();
                    } else {
                        // Open the menu
                        this.handleMouseEnter(menuId);
                        this.updateMenuButtonARIA(button, true);

                        // Move focus to first link in dropdown
                        setTimeout(() => {
                            const menuContent = document.getElementById(`menu-${menuId}`);
                            if (menuContent) {
                                const firstLink = menuContent.querySelector('a[href], button:not([disabled])') as HTMLElement;
                                if (firstLink) {
                                    firstLink.focus();
                                }
                            }
                        }, 50);
                    }
                }
                handled = true;
                break;

            case 'Tab':
                // If menu is open, Tab should move into the dropdown
                if (this.activeMenu === button.dataset.menuId) {
                    e.preventDefault();
                    // Move focus to first link in dropdown
                    const menuContent = document.getElementById(`menu-${button.dataset.menuId}`);
                    if (menuContent) {
                        const firstLink = menuContent.querySelector('a[href], button:not([disabled])') as HTMLElement;
                        if (firstLink) {
                            firstLink.focus();
                        }
                    }
                    handled = true;
                }
                // If menu is not open, let Tab work naturally
                break;

            case 'Escape':
                e.preventDefault();
                if (this.activeMenu) {
                    this.closeActiveMenu();
                    button.focus();
                }
                handled = true;
                break;

            case 'Home':
                e.preventDefault();
                (buttons[0] as HTMLElement).focus();
                handled = true;
                break;

            case 'End':
                e.preventDefault();
                const lastIndex = buttons.length - 1;
                (buttons[lastIndex] as HTMLElement).focus();
                handled = true;
                break;
        }

        return handled;
    }

    closeActiveMenu() {
        this.activeMenu = null;
        this.activeSidebarItem = null;
        this.activeSubSidebarItem = null;
        this.showSubSidebar = false;
        this.isHovering = false;

        // Update all menu buttons ARIA
        const menuButtons = document.querySelectorAll('[data-menu-id]');
        menuButtons.forEach(btn => {
            this.updateMenuButtonARIA(btn as HTMLElement, false);
        });

        // Hide all menu content
        const allMenuContent = document.querySelectorAll('.menu-content');
        allMenuContent.forEach(content => {
            (content as HTMLElement).setAttribute('hidden', '');
        });

        // Announce to screen readers
        this.announceToScreenReader('Meni zaprt');

        this.updateBackdropVisibility();
        this.updateMegaDropdownVisibility();
    }

    handleMobileMenuToggleKeyboard(e: KeyboardEvent) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleMobileMenu();
        }
    }

    updateMobileMenuButtonARIA(expanded: boolean) {
        const toggleButton = document.querySelector('[data-mobile-menu-toggle]') as HTMLElement;
        const mobileMenu = document.querySelector('[data-mobile-menu]') as HTMLElement;

        if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', expanded.toString());
            toggleButton.setAttribute('aria-label', expanded ? 'Zapri meni' : 'Odpri meni');
        }

        // Manage inert attribute to remove mobile menu from tab order when closed
        if (mobileMenu) {
            if (expanded) {
                mobileMenu.removeAttribute('inert');
                this.setupMobileMenuFocusTrap();
            } else {
                mobileMenu.setAttribute('inert', '');
                this.removeMobileMenuFocusTrap();
            }
        }
    }

    // Focus trap for mobile menu
    setupMobileMenuFocusTrap() {
        const mobileMenu = document.querySelector('[data-mobile-menu]') as HTMLElement;
        if (!mobileMenu) return;

        // Get all focusable elements within mobile menu
        const focusableElements = mobileMenu.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstFocusable = focusableElements[0] as HTMLElement;
        const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

        // Store the handler so we can remove it later
        this.mobileMenuFocusTrapHandler = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            }
        };

        mobileMenu.addEventListener('keydown', this.mobileMenuFocusTrapHandler);

        // Focus first element when menu opens
        setTimeout(() => {
            firstFocusable.focus();
        }, 100);
    }

    removeMobileMenuFocusTrap() {
        const mobileMenu = document.querySelector('[data-mobile-menu]') as HTMLElement;
        if (mobileMenu && this.mobileMenuFocusTrapHandler) {
            mobileMenu.removeEventListener('keydown', this.mobileMenuFocusTrapHandler);
            this.mobileMenuFocusTrapHandler = null;
        }
    }

    handleMobileMenuKeydown(e: KeyboardEvent) {
        const target = e.target as HTMLElement;

        // Handle Escape key
        if (e.key === 'Escape') {
            e.preventDefault();
            this.toggleMobileMenu();
            const toggleButton = document.querySelector('[data-mobile-menu-toggle]') as HTMLElement;
            if (toggleButton) {
                toggleButton.focus();
            }
            return;
        }

        // Handle Enter and Space on buttons
        if ((e.key === 'Enter' || e.key === ' ') && target.tagName === 'BUTTON') {
            e.preventDefault();
            target.click();
        }
    }

    // Helper method to determine navigation level
    private getNavigationLevel(element: HTMLElement): 'sidebar' | 'subsidebar' | 'content' | null {
        if (element.hasAttribute('data-sidebar-id')) return 'sidebar';
        if (element.hasAttribute('data-subsidebar-id')) return 'subsidebar';
        if (element.closest('.sidebar-content')) return 'content';
        return null;
    }

    // Helper method to focus on previous navigation level
    private focusPreviousLevel(currentLevel: 'sidebar' | 'subsidebar' | 'content'): boolean {
        const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
        if (!currentMenuContent) return false;

        if (currentLevel === 'subsidebar') {
            // Go back to parent sidebar item
            const activeSidebarItem = currentMenuContent.querySelector(`[data-sidebar-id="${this.activeSidebarItem}"]`) as HTMLElement;
            if (activeSidebarItem) {
                activeSidebarItem.focus();
                return true;
            }
        } else if (currentLevel === 'content') {
            // Check if there's a visible sub-sidebar
            const visibleSubSidebar = currentMenuContent.querySelector('.sub-sidebar-container[style*="display: block"]') as HTMLElement;
            if (visibleSubSidebar) {
                // Go to active sub-sidebar item
                const activeSubSidebarItem = visibleSubSidebar.querySelector(`[data-subsidebar-id="${this.activeSubSidebarItem}"]`) as HTMLElement;
                if (activeSubSidebarItem) {
                    activeSubSidebarItem.focus();
                    return true;
                }
            }
            // No sub-sidebar, go to active sidebar item
            const activeSidebarItem = currentMenuContent.querySelector(`[data-sidebar-id="${this.activeSidebarItem}"]`) as HTMLElement;
            if (activeSidebarItem) {
                activeSidebarItem.focus();
                return true;
            }
        }
        return false;
    }

    // Handle keyboard navigation within dropdown
    handleDropdownKeydown(e: KeyboardEvent) {
        const target = e.target as HTMLElement;

        // Handle Escape key - hierarchical navigation back to previous level
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();

            const level = this.getNavigationLevel(target);

            if (!level) {
                // Not in a recognized navigation element, close menu
                const currentActiveMenu = this.activeMenu;
                this.closeActiveMenu();
                const activeButton = document.querySelector(`[data-menu-id="${currentActiveMenu}"]`) as HTMLElement;
                if (activeButton) {
                    activeButton.focus();
                }
                return;
            }

            // If in subsidebar or content, go back one level
            if (level === 'subsidebar' || level === 'content') {
                if (this.focusPreviousLevel(level)) {
                    return;
                }
            }

            // If in sidebar (top level) or couldn't go back, close the menu
            const currentActiveMenu = this.activeMenu;
            this.closeActiveMenu();
            const activeButton = document.querySelector(`[data-menu-id="${currentActiveMenu}"]`) as HTMLElement;
            if (activeButton) {
                activeButton.focus();
            }
            return;
        }

        // Handle Tab key - manage focus trap within mega menu
        if (e.key === 'Tab') {
            const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
            if (!currentMenuContent) return;

            // Get all focusable elements in dropdown
            const allDropdownLinks = Array.from(currentMenuContent.querySelectorAll('a[href], button:not([disabled])')) as HTMLElement[];
            const visibleDropdownLinks = allDropdownLinks.filter(el => el.offsetParent !== null);

            const currentIndexInDropdown = visibleDropdownLinks.indexOf(target);
            const isFirstInDropdown = currentIndexInDropdown === 0;
            const isLastInDropdown = currentIndexInDropdown === visibleDropdownLinks.length - 1;

            // Forward Tab from last element - wrap to first sidebar item (focus trap)
            if (!e.shiftKey && isLastInDropdown) {
                e.preventDefault();
                const firstSidebarItem = currentMenuContent.querySelector('[data-sidebar-id]') as HTMLElement;
                if (firstSidebarItem) {
                    firstSidebarItem.focus();
                }
                return;
            }

            if (e.shiftKey && isFirstInDropdown) {
                e.preventDefault();
                const currentActiveMenu = this.activeMenu;
                this.closeActiveMenu();
                const activeButton = document.querySelector(`[data-menu-id="${currentActiveMenu}"]`) as HTMLElement;
                if (activeButton) {
                    activeButton.focus();
                }
                return;
            }

            const isInSidebar = target.hasAttribute('data-sidebar-id');
            const isInSubSidebar = target.hasAttribute('data-subsidebar-id');
            const isInContent = !isInSidebar && !isInSubSidebar;

            if (isInSidebar) {
                return;
            } else if (isInSubSidebar) {
                // User is in second level (sub-sidebar)
                const visibleSubSidebar = currentMenuContent.querySelector('.sub-sidebar-container[style*="display: block"]') as HTMLElement;
                if (!visibleSubSidebar) {
                    return;
                }

                const subSidebarFocusableItems = Array.from(
                    visibleSubSidebar.querySelectorAll('a[href], button:not([disabled])')
                ).filter(el => el.offsetParent !== null) as HTMLElement[];
                const currentIndex = subSidebarFocusableItems.indexOf(target as HTMLElement);

                if (!e.shiftKey && currentIndex === subSidebarFocusableItems.length - 1) {
                    // At last sub-sidebar item, Tab moves to content area
                    e.preventDefault();
                    const visibleContent = currentMenuContent.querySelector('.sidebar-content[style*="display: block"]') as HTMLElement;
                    if (visibleContent) {
                        const firstContentLink = visibleContent.querySelector('a[href]') as HTMLElement;
                        if (firstContentLink) {
                            firstContentLink.focus();
                            return;
                        }
                    }
                    // If no content, wrap to first sub-sidebar item
                    subSidebarFocusableItems[0]?.focus();
                    return;
                } else if (e.shiftKey && currentIndex === 0) {
                    // At first sub-sidebar item with Shift+Tab, go back to sidebar
                    e.preventDefault();
                    const activeSidebarItem = currentMenuContent.querySelector(`[data-sidebar-id="${this.activeSidebarItem}"]`) as HTMLElement;
                    if (activeSidebarItem) {
                        activeSidebarItem.focus();
                    }
                    return;
                }
            } else if (isInContent) {
                // User is in third level (content area)
                const visibleContent = currentMenuContent.querySelector('.sidebar-content[style*="display: block"]') as HTMLElement;
                if (visibleContent) {
                    const contentLinks = Array.from(visibleContent.querySelectorAll('a[href]')) as HTMLElement[];
                    const currentIndex = contentLinks.indexOf(target as HTMLElement);

                    // Handle Shift+Tab from first content link - must go back to sidebar/subsidebar
                    if (e.shiftKey && currentIndex === 0) {
                        // At first content link with Shift+Tab, go back to previous level
                        e.preventDefault();

                        // Check if there's a visible sub-sidebar
                        const visibleSubSidebar = currentMenuContent.querySelector('.sub-sidebar-container[style*="display: block"]') as HTMLElement;
                        if (visibleSubSidebar) {
                            // Go back to active sub-sidebar item
                            const activeSubSidebarItem = visibleSubSidebar.querySelector(`[data-subsidebar-id="${this.activeSubSidebarItem}"]`) as HTMLElement;
                            if (activeSubSidebarItem) {
                                activeSubSidebarItem.focus();
                                return;
                            }
                        }

                        // Otherwise go back to active sidebar item
                        const activeSidebarItem = currentMenuContent.querySelector(`[data-sidebar-id="${this.activeSidebarItem}"]`) as HTMLElement;
                        if (activeSidebarItem) {
                            activeSidebarItem.focus();
                        }
                        return;
                    }
                }
            }

            // Let Tab work naturally for middle items
            return;
        }

        // Handle Enter/Space for sidebar items (trigger hover behavior and navigate into submenu)
        if ((e.key === 'Enter' || e.key === ' ') && target.hasAttribute('data-sidebar-id')) {
            e.preventDefault();
            const sidebarId = target.dataset.sidebarId;
            if (sidebarId) {
                // Trigger the hover behavior to show content
                this.handleSidebarItemHover(sidebarId);

                // Announce to screen readers
                const itemName = target.getAttribute('aria-label') || target.textContent?.trim() || '';
                this.announceToScreenReader(`${itemName} selected`);

                // Move focus to the second level content
                const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
                if (currentMenuContent) {
                    // Check if this item has a sub-sidebar
                    const hasSubsidebar = target.dataset.hasSubsidebar === 'true';

                    if (hasSubsidebar) {
                        // Focus on first item in sub-sidebar
                        const subSidebarContainer = currentMenuContent.querySelector(`.sub-sidebar-container[data-subsidebar-for="${sidebarId}"]`);
                        if (subSidebarContainer) {
                            const firstSubSidebarItem = subSidebarContainer.querySelector('a[href], button:not([disabled])') as HTMLElement;
                            if (firstSubSidebarItem) {
                                firstSubSidebarItem.focus();
                                return;
                            }
                        }
                    } else {
                        // Focus on first link in content area for this specific sidebar item
                        const visibleContent = currentMenuContent.querySelector(`.sidebar-content[data-content="${sidebarId}"]`) as HTMLElement;
                        if (visibleContent && visibleContent.style.display !== 'none') {
                            const firstContentLink = visibleContent.querySelector('a[href]') as HTMLElement;
                            if (firstContentLink) {
                                firstContentLink.focus();
                                return;
                            }
                        }
                    }
                }

                // If it's a link and no sub-content, navigate
                if (target.tagName === 'A' && target.hasAttribute('href')) {
                    const href = target.getAttribute('href');
                    if (href && href !== '#') {
                        window.location.href = href;
                    }
                }
            }
            return;
        }

        // Handle Enter/Space for sub-sidebar items
        if ((e.key === 'Enter' || e.key === ' ') && target.hasAttribute('data-subsidebar-id')) {
            e.preventDefault();
            const subsidebarId = target.dataset.subsidebarId;
            if (subsidebarId) {
                // Trigger the hover behavior
                this.handleSubSidebarItemHover(subsidebarId);

                // Announce to screen readers
                const itemName = target.getAttribute('aria-label') || target.textContent?.trim() || '';
                this.announceToScreenReader(`${itemName} selected`);

                // Move focus to the content area (third level) for this specific sub-sidebar item
                const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
                if (currentMenuContent) {
                    const visibleContent = currentMenuContent.querySelector(`.sidebar-content[data-content="${subsidebarId}"]`) as HTMLElement;
                    if (visibleContent && visibleContent.style.display !== 'none') {
                        const firstContentLink = visibleContent.querySelector('a[href]') as HTMLElement;
                        if (firstContentLink) {
                            firstContentLink.focus();
                            return;
                        }
                    }
                }

                // If it's a link and no content area, navigate
                if (target.tagName === 'A' && target.hasAttribute('href')) {
                    const href = target.getAttribute('href');
                    if (href && href !== '#') {
                        window.location.href = href;
                    }
                }
            }
            return;
        }

        // Handle Right Arrow to jump from sidebar to content area
        if (e.key === 'ArrowRight' && target.hasAttribute('data-sidebar-id')) {
            e.preventDefault();
            const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
            if (currentMenuContent) {
                // Find first focusable element in content area
                const contentArea = currentMenuContent.querySelector('.content-area');
                if (contentArea) {
                    const firstContentLink = contentArea.querySelector('a[href]') as HTMLElement;
                    if (firstContentLink) {
                        firstContentLink.focus();
                    }
                }
            }
            return;
        }

        // Handle Left Arrow to jump from content area back to sidebar
        if (e.key === 'ArrowLeft' && !target.hasAttribute('data-sidebar-id') && !target.hasAttribute('data-subsidebar-id')) {
            e.preventDefault();
            const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
            if (currentMenuContent) {
                // Find first visible sidebar item
                const firstSidebarItem = currentMenuContent.querySelector('[data-sidebar-id]') as HTMLElement;
                if (firstSidebarItem) {
                    firstSidebarItem.focus();
                }
            }
            return;
        }

        // Handle Arrow Up/Down for sidebar navigation
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            // Only handle if we're on a sidebar link
            if (target.hasAttribute('data-sidebar-id') || target.hasAttribute('data-subsidebar-id')) {
                e.preventDefault();

                // Get all visible sidebar/subsidebar links in current menu
                const currentMenuContent = document.querySelector(`[data-menu="${this.activeMenu}"]`) as HTMLElement;
                if (!currentMenuContent) return;

                const allLinks = Array.from(currentMenuContent.querySelectorAll('[data-sidebar-id], [data-subsidebar-id]')) as HTMLElement[];
                const visibleLinks = allLinks.filter(link => {
                    const parent = link.closest('.sub-sidebar-container, .sidebar-layout > div');
                    return parent && (parent as HTMLElement).style.display !== 'none';
                });

                const currentIndex = visibleLinks.indexOf(target);
                if (currentIndex === -1) return;

                let nextIndex: number;
                if (e.key === 'ArrowDown') {
                    nextIndex = (currentIndex + 1) % visibleLinks.length;
                } else {
                    nextIndex = (currentIndex - 1 + visibleLinks.length) % visibleLinks.length;
                }

                visibleLinks[nextIndex].focus();
            }
        }
    }

    // Announce menu state changes to screen readers
    announceToScreenReader(message: string) {
        const liveRegion = document.getElementById('menu-announcements');
        if (liveRegion) {
            // Clear previous message
            liveRegion.textContent = '';

            // Set new message after a brief delay to ensure it's announced
            setTimeout(() => {
                liveRegion.textContent = message;
            }, 100);
        }
    }

    // Setup language dropdown to only open on click/Enter/Space, not on focus
    setupLanguageDropdown() {
        const languageToggle = document.querySelector('[data-language-toggle]') as HTMLElement;
        const languageDropdown = languageToggle?.closest('.dropdown') as HTMLElement;
        const languageMenu = document.getElementById('language-menu') as HTMLElement;

        if (!languageToggle || !languageDropdown || !languageMenu) return;

        // Handle click to toggle dropdown
        languageToggle.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isOpen = languageDropdown.classList.contains('dropdown-open');

            if (isOpen) {
                this.closeLanguageDropdown(languageDropdown, languageToggle, languageMenu);
            } else {
                this.openLanguageDropdown(languageDropdown, languageToggle, languageMenu);
            }
        });

        // Handle keyboard events (Enter and Space)
        languageToggle.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                const isOpen = languageDropdown.classList.contains('dropdown-open');

                if (isOpen) {
                    this.closeLanguageDropdown(languageDropdown, languageToggle, languageMenu);
                } else {
                    this.openLanguageDropdown(languageDropdown, languageToggle, languageMenu);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.closeLanguageDropdown(languageDropdown, languageToggle, languageMenu);
                languageToggle.focus();
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!languageDropdown.contains(e.target as Node)) {
                this.closeLanguageDropdown(languageDropdown, languageToggle, languageMenu);
            }
        });

        // Handle keyboard navigation within the menu
        languageMenu.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.closeLanguageDropdown(languageDropdown, languageToggle, languageMenu);
                languageToggle.focus();
            }
        });
    }

    openLanguageDropdown(dropdown: HTMLElement, toggle: HTMLElement, menu: HTMLElement) {
        dropdown.classList.add('dropdown-open');
        toggle.setAttribute('aria-expanded', 'true');
        menu.removeAttribute('hidden');

        // Focus first menu item
        setTimeout(() => {
            const firstMenuItem = menu.querySelector('a[role="menuitem"]') as HTMLElement;
            if (firstMenuItem) {
                firstMenuItem.focus();
            }
        }, 50);
    }

    closeLanguageDropdown(dropdown: HTMLElement, toggle: HTMLElement, menu: HTMLElement) {
        dropdown.classList.remove('dropdown-open');
        toggle.setAttribute('aria-expanded', 'false');
        menu.setAttribute('hidden', '');
    }

    init() {
        // Initialize all dropdown elements with tabindex="-1" (hidden by default)
        const allMenuContent = document.querySelectorAll('.menu-content');
        allMenuContent.forEach(content => {
            this.updateDropdownTabindex(content as HTMLElement, true);
        });

        // Also initialize any sidebar/content elements that don't have tabindex yet
        const allSidebarItems = document.querySelectorAll('[data-sidebar-id]:not([tabindex])');
        const allSubSidebarItems = document.querySelectorAll('[data-subsidebar-id]:not([tabindex])');
        const allContentLinks = document.querySelectorAll('.content-area a[href]:not([data-sidebar-id]):not([data-subsidebar-id]):not([tabindex])');

        [...allSidebarItems, ...allSubSidebarItems, ...allContentLinks].forEach(element => {
            (element as HTMLElement).setAttribute('tabindex', '-1');
        });

        // Setup event listeners for desktop menu
        const menuButtons = document.querySelectorAll('[data-menu-id]');
        menuButtons.forEach((button, index) => {
            // Click event (Toggle menu)
            button.addEventListener('click', (e) => {
                e.preventDefault(); // Prevent default link behavior if any
                e.stopImmediatePropagation(); // STRICTLY prevent any other listeners (including document)

                const target = e.currentTarget as HTMLElement;
                const menuId = target.dataset.menuId;

                if (!menuId) return;

                // Check if this menu is currently open
                if (this.activeMenu === menuId) {
                    // Close if already open
                    this.closeActiveMenu();
                    this.updateMenuButtonARIA(button as HTMLElement, false);
                } else {
                    // Open if closed (this will also close any other open menu via activeMenu logic in handleMouseEnter)
                    this.handleMouseEnter(menuId);
                    this.updateMenuButtonARIA(button as HTMLElement, true);
                }
            });

            // Keyboard events
            button.addEventListener('keydown', (e) => {
                this.handleMenuButtonKeydown(e as KeyboardEvent, button as HTMLElement, menuButtons, index);
            });
        });

        // Initialize mega dropdown element
        this.megaDropdownElement = document.querySelector('[data-mega-dropdown]');

        // Setup mega menu dropdown event listeners
        if (this.megaDropdownElement) {

            // Add keyboard handler for mega menu dropdown
            this.megaDropdownElement.addEventListener('keydown', (e) => {
                this.handleDropdownKeydown(e as KeyboardEvent);
            });
        }

        // Setup mobile menu toggle button event listener
        const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
        if (mobileMenuToggle) {
            mobileMenuToggle.addEventListener('click', () => this.toggleMobileMenu());
            mobileMenuToggle.addEventListener('keydown', (e) => this.handleMobileMenuToggleKeyboard(e as KeyboardEvent));
        }

        // Setup mobile menu keyboard navigation
        const mobileMenu = document.querySelector('[data-mobile-menu]');
        if (mobileMenu) {
            mobileMenu.addEventListener('keydown', (e) => this.handleMobileMenuKeydown(e as KeyboardEvent));
        }

        // Setup initial mobile menu event listeners
        this.setupMobileMenuEventListeners();

        // Setup language dropdown
        this.setupLanguageDropdown();

        // Initialize Headroom.js
        this.navElement = document.querySelector('nav[data-mega-nav]');
        if (this.navElement && typeof window !== 'undefined' && (Headroom || window.Headroom)) {
            const HeadroomClass = Headroom || window.Headroom;
            this.headroom = new HeadroomClass(this.navElement, {
                offset: 50,
                tolerance: {
                    up: 5,
                    down: 10
                },
                classes: {
                    initial: "headroom",
                    pinned: "headroom--pinned",
                    unpinned: "headroom--unpinned",
                    top: "headroom--top",
                    notTop: "headroom--not-top",
                    bottom: "headroom--bottom",
                    notBottom: "headroom--not-bottom",
                    frozen: "headroom--frozen"
                }
            });
            this.headroom.init();
        }

        // Close desktop mega menu on scroll
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', () => {
                if (this.activeMenu) {
                    // Close the menu immediately when user scrolls
                    this.closeActiveMenu();
                }
            });

            // Global Escape key handler
            document.addEventListener('keydown', (e) => {
                // Prevent scrolling when pressing Space on ANY link
                if (e.key === ' ' && (e.target as HTMLElement).tagName === 'A') {
                    e.preventDefault();
                    (e.target as HTMLElement).click();
                    return;
                }

                if (e.key === 'Escape') {
                    if (this.mobileMenuOpen) {
                        e.preventDefault();
                        this.toggleMobileMenu();
                        // Return focus to mobile menu toggle
                        const toggleButton = document.querySelector('[data-mobile-menu-toggle]') as HTMLElement;
                        if (toggleButton) {
                            toggleButton.focus();
                        }
                    } else if (this.activeMenu) {
                        e.preventDefault();
                        const currentActiveMenu = this.activeMenu;
                        this.closeActiveMenu();
                        // Return focus to the button that opened the menu
                        const activeButton = document.querySelector(`[data-menu-id="${currentActiveMenu}"]`) as HTMLElement;
                        if (activeButton) {
                            activeButton.focus();
                        }
                    }
                }
            });

            // Click outside to close
            document.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const isInsideNav = target.closest('[data-mega-nav]');
                const isInsideDropdown = target.closest('[data-mega-dropdown]');
                const isInsideMobileMenu = target.closest('[data-mobile-menu]');

                if (!isInsideNav && !isInsideDropdown && !isInsideMobileMenu) {
                    if (this.activeMenu) {
                        this.closeActiveMenu();
                    }
                    if (this.mobileMenuOpen) {
                        this.toggleMobileMenu();
                    }
                }
            });
        }
    }
}
